import React, { FC, useContext, useState } from 'react';

import { Typography } from '@material-ui/core';
import AddBoxIcon from '@material-ui/icons/AddBox';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';

import { Column, Filter, Query } from 'material-table';
import EntityFormsModal from './EntityForms';
import { materialTableLocaleData } from '../../locale/material-table';
import MaterialTableComponent from '../../common/components/MaterialTableComponent';
import { useGetAllEchelons } from '../../hooks/queries';
import { useDeleteEntity } from '../../hooks/mutations';
import MTableSelectFilterComponent from '../../common/components/MaterialTableComponents/MTableSelectFilterComponent';
import MTableFilterComponent from '../../common/components/MaterialTableComponents/MTableFilterComponent';
import { IUserContext, UserContext } from '../../providers/UserProvider';
import { Operation, Resource } from '../../common/constants';
import ConfirmationModal from '../../common/components/ConfirmationModal';
import { formatSIRET } from '../../common/validator';
import PublishIcon from '@material-ui/icons/Publish';
import EntitiesCSVImportationModal from './EntitiesCSVImportationModal/EntitiesCSVImportationModal';
import GetAppIcon from '@material-ui/icons/GetApp';
import {
  EntityOrderByInput,
  EntityWhereInput,
  SortOrder,
} from '../../types/graphql-global-types';
import { useApolloClient } from '@apollo/react-hooks';
import {
  AllEntities,
  AllEntities_allEntities_entities,
  AllEntitiesVariables,
} from '../../graphql/entity/types/AllEntities';
import { GET_ALL_ENTITIES } from '../../graphql/entity/query';
import { API_URL } from '../../config';

interface EntitiesProps {}

const Entities: FC<EntitiesProps> = () => {
  const client = useApolloClient();

  const { canI } = useContext<IUserContext>(UserContext);

  const canCreateEntity = canI(Operation.CREATE, Resource.ENTITIES);
  const canEditEntity = canI(Operation.UPDATE, Resource.ENTITIES);
  // const canDeleteEntity = canI(Operation.DELETE, Resource.ENTITIES);
  const canImportEntities = canI(Operation.IMPORT, Resource.ENTITIES);
  const canExportEntities = canI(Operation.EXPORT, Resource.ENTITIES);

  const [openCSVImport, setOpenCSVImport] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [
    selectedEntity,
    setSelectedEntity,
  ] = useState<AllEntities_allEntities_entities>();

  const [deleteEntities] = useDeleteEntity();

  const echelons = useGetAllEchelons();

  const tableColumns = [
    {
      title: 'idCC',
      editable: 'never',
      field: 'id',
      searchable: false,
      width: 80,
      filterComponent: ({ columnDef, onFilterChanged }: any) => (
        <MTableFilterComponent
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      ),
    },
    {
      title: 'idCRM',
      editable: 'never',
      field: 'idCRM',
      searchable: false,
      width: 80,
      filterComponent: ({ columnDef, onFilterChanged }: any) => (
        <MTableFilterComponent
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      ),
    },
    {
      title: 'Nom',
      field: 'name',
      emptyValue: '-',
      defaultGroupSort: 'asc',
      defaultSort: 'asc',
      filtering: false,
      grouping: false,
    },
    {
      title: 'Adresse',
      field: 'adress',
      emptyValue: '-',
      filterComponent: ({ columnDef, onFilterChanged }: any) => (
        <MTableFilterComponent
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      ),
    },
    {
      title: 'Code postal',
      field: 'zipCode',
      emptyValue: '-',
      filterComponent: ({ columnDef, onFilterChanged }: any) => (
        <MTableFilterComponent
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      ),
    },
    {
      title: 'Ville',
      field: 'town',
      emptyValue: '-',
      filterComponent: ({ columnDef, onFilterChanged }: any) => (
        <MTableFilterComponent
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      ),
    },
    {
      title: 'Mail',
      field: 'email',
      emptyValue: '-',
      filtering: false,
    },
    {
      title: 'Téléphone',
      field: 'phone',
      emptyValue: '-',
      filtering: false,
    },
    {
      title: 'SIRET',
      field: 'siret',
      render: (dataRow) => (dataRow.siret ? formatSIRET(dataRow.siret) : '-'),
      filtering: false,
    },
    {
      title: 'Echelon',
      field: 'echelon.name',
      emptyValue: '-',
      filterable: true,
      searchable: false,
      filterComponent: ({ columnDef, onFilterChanged }: any) => {
        return (
          <MTableSelectFilterComponent
            options={
              echelons?.data?.getAllEchelons?.reduce<string[]>(
                (acc, cur) => (cur?.name && acc.push(cur?.name) && acc) || acc,
                [],
              ) ?? []
            }
            labels={{}}
            onChange={(value) =>
              onFilterChanged(columnDef?.tableData?.id, value)
            }
          />
        );
      },
    },
    {
      title: 'Enseigne',
      field: 'enseigne.name',
      emptyValue: '-',
      editable: 'onAdd',
      filterable: true,
      searchable: false,
      filterComponent: ({ columnDef, onFilterChanged }: any) => (
        <MTableFilterComponent
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      ),
    },
    {
      title: 'Entité parente',
      field: 'parentEntity.name',
      emptyValue: '-',
      filterable: true,
      searchable: false,
      filterComponent: ({ columnDef, onFilterChanged }: any) => (
        <MTableFilterComponent
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      ),
    },
    {
      title: 'Réseau',
      field: 'reseau.name',
      emptyValue: '-',
      filterable: true,
      searchable: false,
      filterComponent: ({ columnDef, onFilterChanged }: any) => (
        <MTableFilterComponent
          onChange={(value) => onFilterChanged(columnDef?.tableData?.id, value)}
        />
      ),
    },
  ] as Array<Column<AllEntities_allEntities_entities>>;

  const handleDelete = () => {
    if (selectedEntity)
      deleteEntities({
        variables: { entityIds: [selectedEntity?.id] },
      }).then(() => {
        setSelectedEntity(undefined);
        setOpenDeleteDialog(false);
      });
  };

  const handleCloseCSVImport = () => {
    setOpenCSVImport(false);
  };

  return (
    <>
      <MaterialTableComponent
        localization={{
          ...materialTableLocaleData.fr,
          header: {
            actions: 'Actions',
          },
        }}
        actions={[
          {
            icon: () => <GetAppIcon />,
            onClick: (_, __) => {
              window.open(API_URL + '/api/v1/download/entities');
            },
            isFreeAction: true,
            tooltip: 'Exporter sous Excel',
            disabled: !canExportEntities,
          },
          {
            icon: () => <PublishIcon />,
            onClick: (_, __) => {
              setOpenCSVImport(true);
            },
            isFreeAction: true,
            tooltip: 'Importer depuis un fichier CSV',
            disabled: !canImportEntities,
          },
          {
            icon: () => <AddBoxIcon />,
            onClick: (_, __) => {
              setSelectedEntity(undefined);
              setOpen(true);
            },
            isFreeAction: true,
            tooltip: 'Créer une nouvelle entité',
            disabled: !canCreateEntity,
          },
          {
            icon: () => <EditIcon />,
            tooltip: 'Editer',
            onClick: (event, rowData) => {
              setOpen(true);
              setSelectedEntity(rowData);
            },
            disabled: !canEditEntity,
          },
          (rowData) => ({
            icon: () => <DeleteIcon />,
            tooltip: rowData?.linkedWithSomething ? undefined : 'Supprimer',
            onClick: (event, rowData) => {
              setSelectedEntity(rowData);
              setOpenDeleteDialog(true);
            },
            disabled: rowData?.linkedWithSomething,
          }),
        ]}
        title="Liste des entités"
        columns={tableColumns}
        data={(query) =>
          new Promise(async (resolve) => {
            const { page, pageSize } = query;
            const search = query?.search?.trim() || undefined;

            const filters = query?.filters?.reduce<EntityWhereInput>(
              (acc, filter) => ({
                ...acc,
                ...formatFilter(filter),
              }),
              {},
            );

            const { data } = await client.query<
              AllEntities,
              AllEntitiesVariables
            >({
              fetchPolicy: 'network-only',
              query: GET_ALL_ENTITIES,
              variables: {
                input: {
                  take: pageSize,
                  skip: page * pageSize,
                  where: {
                    name: search,
                    phone: search,
                    email: search,
                    ...filters,
                  },
                  orderBy: formatOrderBy(query),
                },
              },
            });

            const totalCount = data?.allEntities?.totalCount ?? 0;
            const businesses = data?.allEntities?.entities ?? [];

            resolve({
              totalCount,
              page,
              data: businesses,
            });
          })
        }
        isLoading={false}
        options={{
          filtering: true,
          search: true,
          addRowPosition: 'first',
          pageSize: 10,
        }}
      />

      {open && (
        <EntityFormsModal
          title="Création entité"
          loading={isLoading}
          open={open}
          setIsLoading={setIsLoading}
          onCancel={() => setOpen(false)}
          onCreate={() => setOpen(false)}
          entity={selectedEntity}
        />
      )}

      {openCSVImport && (
        <EntitiesCSVImportationModal handleClose={handleCloseCSVImport} />
      )}
      <ConfirmationModal
        content={
          <Typography>Voulez-vous vraiment supprimer cette entité ?</Typography>
        }
        confirmLabel="Oui, supprimer"
        open={openDeleteDialog}
        onConfirm={handleDelete}
        onCancel={() => {
          setSelectedEntity(undefined);
          setOpenDeleteDialog(false);
        }}
      />
    </>
  );
};

const formatFilter = (filter: Filter<any>): EntityWhereInput => {
  const key = filter?.column?.field as string;
  const value = filter?.value || undefined;

  const _parseNumber = (n: string | null | undefined): number | undefined =>
    n && !isNaN(+n) ? +n : undefined;

  const mapping: { [p: string]: any } = {
    id: { idCC: _parseNumber(value) },
    idCRM: { idCRM: _parseNumber(value) },
    adress: { address: value },
    'echelon.name': { echelon: { name: value } },
    'enseigne.name': { enseignes: [{ name: value }] },
    'parentEntity.name': { parent: { name: value } },
    'reseau.name': { network: { name: value } },
  };

  return mapping[key] || { [key]: value };
};

const formatOrderBy = (query: Query<any>): EntityOrderByInput | undefined => {
  const key = query?.orderBy?.field;
  const direction = query?.orderDirection as SortOrder;

  if (!key || !direction) return undefined;

  const mapping: { [p: string]: any } = {
    id: { idCC: direction },
    adress: { address: direction },
    'echelon.name': { echelon: direction },
    'enseigne.name': { enseigne: { name: direction } },
    'parentEntity.name': { parent: { name: direction } },
    'reseau.name': { network: { name: direction } },
  };

  return mapping[key as string] || { [key]: direction };
};

export default Entities;
